// ** React imports
import { useState, useEffect } from "react";

// ** MUI Imports
import {
  Box,
  Button,
  Typography,
  Grid,
  Popover,
  FormLabel,
  IconButton,
  useTheme
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import { DataGridPro } from '@mui/x-data-grid-pro';

// ** Icon Imports
import Icon from "components/icon";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import MuiArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import LaborIcon from "@mui/icons-material/Engineering";
import OtherCostsIcon from "@mui/icons-material/PaidOutlined";

// ** Logic Imports
// import { useLineItemLogic } from "../logic/line-item-logic";
import AddLineItem from "./AddLineItem";
import { GridColumns } from "./GridColumns";

//#region Styled Components
const ArrowDropDownIcon = styled(MuiArrowDropDownIcon)({
  fontSize: "20px",
  width: "20px",
  height: "20px",
});

const dropdownItemsStyle = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  px: 4,
  py: 2,
  "&:hover": {
    backgroundColor: "action.hover",
    cursor: "pointer",
  },
};

function mapMaterialActualRow(row) {
  return {
    id: row.jobMaterialID,
    jobMaterialID: row.jobMaterialID,
    jobID: row.jobID,
    materialID: row.materialID,
    materialTypeID: row.materialTypeID,
    sku: row.sku,
    unitOfMeasure: row.unitOfMeasure,
    budgetedQuantity: row.budgetedQuantity,
    budgetedCostPerUnit: row.budgetedCostPerUnit,
    budgetedPricePerUnit: row.budgetedPricePerUnit,
    actualQuantity: row.actualQuantity,
    actualCostPerUnit: row.actualCostPerUnit,
    actualPricePerUnit: row.actualPricePerUnit,
    providedBy: row.providedBy,
    sortOrder: row.sortOrder,
    notes: row.notes,
    itemType: row.materialTypeName,
    itemName: row.materialName,
    unit: row.unitOfMeasure,
    unitQuantity: row.actualQuantity,
    modifiedbyname: row.modifiedByName,
    unitCost: row.actualCostPerUnit,
    totalCost: row.actualTotalCost,
    variance: row.totalCostVariance,
  };
}

function mapOtherCostActualRow(row) {
  return {
    id: row.miscCostID,
    miscCostID: row.miscCostID,
    jobID: row.jobID,
    costName: row.costName,
    costDescription: row.costDescription,
    billToCustomer: row.billToCustomer,
    sortOrder: row.sortOrder,
    budgetedCostAmount: row.budgetedCostAmount,
    budgetedCustomerPrice: row.budgetedCustomerPrice,
    actualCostAmount: row.actualCostAmount,
    actualCustomerPrice: row.actualCustomerPrice,
    unitCost: row.actualCostAmount,
    totalCost: row.actualCustomerPrice,
    modifiedbyname: row.modifiedByName,
    variance: row.costVariance,
  };
}

function mapAssetActualRow(row) {
  return {
    id: row.jobAssetID,
    jobAssetID: row.jobAssetID,
    jobID: row.jobID,
    assetID: row.assetID,
    assetTypeID: row.assetTypeID,
    budgetedQuantity: row.budgetedQuantity,
    budgetedCostPerUnit: row.budgetedCostPerUnit,
    budgetedDuration: row.budgetedDuration,
    budgetedPricePerUnit: row.budgetedPricePerUnit,
    actualQuantity: row.actualQuantity,
    actualCostPerUnit: row.actualCostPerUnit,
    actualDuration: row.actualDuration,
    actualPricePerUnit: row.actualPricePerUnit,
    billToCustomer: row.billToCustomer,
    billingUnit: row.billingUnit,
    rentalStatus: row.rentalStatus,
    providedBy: row.providedBy,
    sortOrder: row.sortOrder,
    notes: row.notes,
    assetTypeName: row.assetTypeName,
    assetName: row.assetName,
    quantity: row.actualQuantity,
    modifiedbyname: row.modifiedByName,
    duration: row.actualDuration,
    unitCost: row.actualCostPerUnit,
    totalCost: row.actualTotalCost,
  };
}

function mapLaborActualRow(row) {
  return {
    id: row.jobLaborID,
    jobLaborID: row.jobLaborID,
    jobID: row.jobID,
    skillID: row.skillID,
    employeeID: row.employeeID,
    teamID: row.teamID,
    budgetedCostPerHour: row.budgetedCostPerHour,
    budgetedHours: row.budgetedHours,
    actualCostPerHour: row.actualCostPerHour,
    actualHours: row.actualHours,
    markup: row.markup,
    providedBy: row.providedBy,
    sortOrder: row.sortOrder,
    notes: row.notes,
    skillType: row.skillType,
    employee: row.employeeName,
    hourlyRate: row.actualCostPerHour,
    manHours: row.actualHours,
    modifiedbyname: row.modifiedByName,
    totalCost: row.actualTotalCost,
    varianceHrs: row.hoursVariance,
    varianceAmnt: row.totalCostVariance,
  };
}

function mapSubcontractorActualRow(row) {
  return {
    id: row.jobSubcontractorID,
    jobSubcontractorID: row.jobSubcontractorID,
    jobID: row.jobID,
    skillID: row.skillID,
    skillType: row.skillType,
    employeeID: row.employeeID,
    employeeName: row.employeeName,
    teamID: row.teamID,
    unitType: row.unitType,
    actualUnitQuantity: row.actualUnitQuantity,
    actualCostPerUnit: row.actualCostPerUnit,
    actualPricePerUnit: row.actualPricePerUnit,
    actualProfitPerUnit: row.actualProfitPerUnit,
    actualTotalCost: row.actualTotalCost,
    actualTotalPrice: row.actualTotalPrice,
    actualTotalProfit: row.actualTotalProfit,
    actualMarkup: row.actualMarkup,
    providedByName: row.providedByName,
    modifiedbyname: row.modifiedByName,
    sortOrder: row.sortOrder,
    notes: row.notes,
  };
}

// BvA mapping functions for DataGrid columns
function mapMaterialBvARow(row) {
  // Use calculated values for total cost and total price
  const budgetedTotalCost = Number(row.budgetedTotalCost) || 0;
  const actualTotalCost = Number(row.actualTotalCost) || 0;
  const budgetedTotalPrice = Number(row.budgetedTotalPrice) || 0;
  const actualTotalPrice = Number(row.actualTotalPrice) || 0;

  const costVariance = actualTotalCost - budgetedTotalCost;
  const costVariancePercentage = budgetedTotalCost !== 0 ? (((actualTotalCost - budgetedTotalCost) / budgetedTotalCost) * 100).toFixed(2) + '%' : '';
  const priceVariance = actualTotalPrice - budgetedTotalPrice;
  const priceVariancePercentage = budgetedTotalPrice !== 0 ? (((actualTotalPrice - budgetedTotalPrice) / budgetedTotalPrice) * 100).toFixed(2) + '%' : '';

  return {
    id: row.jobMaterialID,
    itemName: row.materialName,
    unit: row.unitOfMeasure,
    budgetQuantity: row.budgetedQuantity,
    actualQuantity: row.actualQuantity,
    budgetCost: row.budgetedCostPerUnit,
    actualCost: row.actualCostPerUnit,
    budgetTotalCost: budgetedTotalCost,
    actualTotalCost: actualTotalCost,
    costVariance: costVariance,
    costVariancePercentage: costVariancePercentage,
    budgetedPricePerUnit: row.budgetedPricePerUnit,
    actualPricePerUnit: row.actualPricePerUnit,
    budgetedTotalPrice: budgetedTotalPrice,
    actualTotalPrice: actualTotalPrice,
    priceVariance: priceVariance,
    priceVariancePercentage: priceVariancePercentage,
    notes: row.notes,
  };
}

function mapLaborBvARow(row) {
  // Use calculated values for total cost and total price
  const budgetedTotalCost = Number(row.budgetedTotalCost) || 0;
  const actualTotalCost = Number(row.actualTotalCost) || 0;
  const budgetedTotalPrice = Number(row.budgetedTotalPrice) || 0;
  const actualTotalPrice = Number(row.actualTotalPrice) || 0;

  const costVariance = actualTotalCost - budgetedTotalCost;
  const costVariancePercentage = budgetedTotalCost !== 0 ? (((actualTotalCost - budgetedTotalCost) / budgetedTotalCost) * 100).toFixed(2) + '%' : '';
  const priceVariance = actualTotalPrice - budgetedTotalPrice;
  const priceVariancePercentage = budgetedTotalPrice !== 0 ? (((actualTotalPrice - budgetedTotalPrice) / budgetedTotalPrice) * 100).toFixed(2) + '%' : '';

  return {
    id: row.jobLaborID,
    employee: row.employeeName,
    skillType: row.skillType,
    budgetManHours: row.budgetedHours,
    actualManHours: row.actualHours,
    budgetCost: row.budgetedCostPerHour,
    actualCost: row.actualCostPerHour,
    budgetTotalCost: budgetedTotalCost,
    actualTotalCost: actualTotalCost,
    costVariance: costVariance,
    costVariancePercentage: costVariancePercentage,
    budgetedPricePerUnit: row.budgetedPricePerUnit,
    actualPricePerUnit: row.actualPricePerUnit,
    budgetedTotalPrice: budgetedTotalPrice,
    actualTotalPrice: actualTotalPrice,
    priceVariance: priceVariance,
    priceVariancePercentage: priceVariancePercentage,
    notes: row.notes,
  };
}

function mapSubcontractorBvARow(row) {
  // Use calculated values for total cost and total price
  const budgetedTotalCost = Number(row.budgetedTotalCost) || 0;
  const actualTotalCost = Number(row.actualTotalCost) || 0;
  const budgetedTotalPrice = Number(row.budgetedTotalPrice) || 0;
  const actualTotalPrice = Number(row.actualTotalPrice) || 0;

  const costVariance = actualTotalCost - budgetedTotalCost;
  const costVariancePercentage = budgetedTotalCost !== 0 ? (((actualTotalCost - budgetedTotalCost) / budgetedTotalCost) * 100).toFixed(2) + '%' : '';
  const priceVariance = actualTotalPrice - budgetedTotalPrice;
  const priceVariancePercentage = budgetedTotalPrice !== 0 ? (((actualTotalPrice - budgetedTotalPrice) / budgetedTotalPrice) * 100).toFixed(2) + '%' : '';

  return {
    id: row.jobSubcontractorID,
    employeeName: row.employeeName,
    skillType: row.skillType,
    unitType: row.unitType,
    budgetedUnitQuantity: row.budgetedUnitQuantity,
    actualUnitQuantity: row.actualUnitQuantity,
    budgetedCostPerUnit: row.budgetedCostPerUnit,
    actualCostPerUnit: row.actualCostPerUnit,
    budgetedTotalCost: budgetedTotalCost,
    actualTotalCost: actualTotalCost,
    costVariance: costVariance,
    costVariancePercentage: costVariancePercentage,
    budgetedPricePerUnit: row.budgetedPricePerUnit,
    actualPricePerUnit: row.actualPricePerUnit,
    budgetedTotalPrice: budgetedTotalPrice,
    actualTotalPrice: actualTotalPrice,
    priceVariance: priceVariance,
    priceVariancePercentage: priceVariancePercentage,
    notes: row.notes,
  };
}

function mapAssetBvARow(row) {
  // Use calculated values for total cost and total price
  const budgetedTotalCost = Number(row.budgetedTotalCost) || 0;
  const actualTotalCost = Number(row.actualTotalCost) || 0;
  const budgetedTotalPrice = Number(row.budgetedTotalPrice) || 0;
  const actualTotalPrice = Number(row.actualTotalPrice) || 0;

  // $ sign formatting for price fields
  const formatUSD = (val) => val !== null && val !== undefined ? `$${Number(val).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : '';

  const costVariance = actualTotalCost - budgetedTotalCost;
  const costVariancePercentage = budgetedTotalCost !== 0 ? (((actualTotalCost - budgetedTotalCost) / budgetedTotalCost) * 100).toFixed(2) + '%' : '';
  const priceVariance = actualTotalPrice - budgetedTotalPrice;
  const priceVariancePercentage = budgetedTotalPrice !== 0 ? (((actualTotalPrice - budgetedTotalPrice) / budgetedTotalPrice) * 100).toFixed(2) + '%' : '';

  return {
    id: row.jobAssetID,
    assetName: row.assetName,
    assetType: row.assetTypeName || row.assetTypeID || '',
    budgetUsageDuration: row.budgetedDuration,
    actualUsageDuration: row.actualDuration,
    unit: row.billingUnit || row.unitOfMeasure || '',
    budgetQuantity: row.budgetedQuantity,
    actualQuantity: row.actualQuantity,
    budgetCost: row.budgetedCostPerUnit,
    actualCost: row.actualCostPerUnit,
    budgetTotalCost: budgetedTotalCost,
    actualTotalCost: actualTotalCost,
    costVariance: costVariance,
    costVariancePercentage: costVariancePercentage,
    budgetedPricePerUnit: row.budgetedPricePerUnit,
    actualPricePerUnit: row.actualPricePerUnit,
    budgetedTotalPrice: formatUSD(budgetedTotalPrice),
    actualTotalPrice: formatUSD(actualTotalPrice),
    priceVariance: formatUSD(priceVariance),
    priceVariancePercentage: priceVariancePercentage,
    notes: row.notes,
  };
}

function mapOtherCostBvARow(row) {
  // Use calculated values for total cost and price
  const budgetedTotalCost = Number(row.budgetedCostAmount) || 0;
  const actualTotalCost = Number(row.actualCostAmount) || 0;
  const budgetedTotalPrice = Number(row.budgetedCustomerPrice) || 0;
  const actualTotalPrice = Number(row.actualCustomerPrice) || 0;

  const costVariance = actualTotalCost - budgetedTotalCost;
  const costVariancePercentage = budgetedTotalCost !== 0 ? (((actualTotalCost - budgetedTotalCost) / budgetedTotalCost) * 100).toFixed(2) + '%' : '';
  const priceVariance = actualTotalPrice - budgetedTotalPrice;
  const priceVariancePercentage = budgetedTotalPrice !== 0 ? (((actualTotalPrice - budgetedTotalPrice) / budgetedTotalPrice) * 100).toFixed(2) + '%' : '';

  return {
    id: row.miscCostID,
    costName: row.costName,
    budgetCost: row.budgetedCostAmount,
    actualCost: row.actualCostAmount,
    budgetTotalCost: budgetedTotalCost,
    actualTotalCost: actualTotalCost,
    costVariance: costVariance,
    costVariancePercentage: costVariancePercentage,
    budgetedPrice: budgetedTotalPrice,
    actualPrice: actualTotalPrice,
    priceVariance: priceVariance,
    priceVariancePercentage: priceVariancePercentage,
    notes: row.notes,
  };
}
//#endregion Styled Components

const LineItems = ({ 
  // entity, rules, workReq, setLoading, toast, job, disableEdit = false, forceEmpty = false 
  entity,
  workReq,
  job,
  rules,
  setLoading,
  toast,
  disableEdit = false,
  forceEmpty = false,

  // ** all of these now come _in_ from the parent **
  LineItemProps,
  LineItemObservationProps,
  LineItemMaterialProps,
  LineItemLaborProps,
  LineItemSubcontractorProps,
  LineItemMiscCostProps,

  setLineItemChanged
}) => {
  const theme = useTheme();

  //#region States and Contants
  const buttonOptions = [
    { name: "Material" },
    { name: "Labor" },
    { name: "Assets" },
    { name: "Other Cost" },
    { name: "Subcontractor" }
  ];
  const buttonOptionsBvA = [
    { name: "Material" },
    { name: "Labor" },
    { name: "Assets" },
    { name: "Other Cost" },
    { name: "Subcontractor" }
  ];
  const [buttons, setButtons] = useState(
    entity === 'jobDetailBvA' ? buttonOptionsBvA : 
    entity === 'jobDetail' ? buttonOptions : 
    buttonOptions
  );

  const [selectedButton, setSelectedButton] = useState(0);
  const [buttonName, setButtonName] = useState("Material");
  const [filteredRows, setFilteredRows] = useState([]);

  // const {LineItemProps, LineItemObservationProps, LineItemMaterialProps, LineItemLaborProps, LineItemMiscCostProps} = useLineItemLogic({entity, workReq, setLoading, toast, job});
  const {
    handleAddLineItemOpenAsset,
    handleAddLineItemOpenObservation,
    handleAddLineItemOpenLabor,
    handleAddLineItemOpenSubcontractor,
    handleAddLineItemOpenMaterial,
    handleAddLineItemOpenOtherCosts,
    openAddLineItem,
    handleLineItemClose,
    jobAssetList,
    handleJobAssetEdit,
    handleJobAssetDelete,
  } = LineItemProps;

  const {
    jobObservationList,
    handleJobObservationDelete,
    handleJobObservationEdit,
  } = LineItemObservationProps;

  const {
    jobMaterialList,
    handleJobMaterialDelete,
    handleJobMaterialEdit,
    fetchJobMaterials,
  } = LineItemMaterialProps;

  const {
    jobLaborList,
    handleJobLaborDelete,
    handleJobLaborEdit,
    employeeList,
  } = LineItemLaborProps;

  const {
    jobSubcontractorList,
    handleJobSubcontractorDelete,
    handleJobSubcontractorEdit,
    subcontractorList
  } = LineItemSubcontractorProps;

  const {
    jobMiscCostList,
    handleJobMiscCostDelete,
    handleJobMiscCostEdit,
  } = LineItemMiscCostProps;

  const {
    columnsMaterial,
    columnsActualMaterial,
    columnsActualvsBudgetMaterial,
    columnsLabor,
    columnsActualLabor,
    columnsActualvsBudgetLabor,
    columnsSubcontractor,
    columnsAssets,
    columnsAssetsDetail,
    columnsActualAssets,
    columnsActualvsBudgetAssets,
    columnsOtherCost,
    columnsActualOtherCost,
    columnsActualvsBudgetOtherCost,
    columnsActualSubContractor,
    columnsActualvsBudgetSubContractor,
  } = GridColumns({ 
    handleJobAssetEdit, 
    handleJobAssetDelete,
    handleJobMaterialDelete,
    handleJobMaterialEdit,
    handleJobMiscCostDelete,
    handleJobMiscCostEdit,
    handleJobLaborDelete,
    handleJobLaborEdit,
    handleJobSubcontractorDelete,
    handleJobSubcontractorEdit,
    disableEdit,
  });

  useEffect(() => {
    setLineItemChanged(openAddLineItem)
  }, [openAddLineItem]);

  const [columns, setColumns] = useState(
    entity === 'jobDetail' || entity === 'workOrder' ? columnsMaterial :
    entity === 'jobDetailActual' ? columnsActualMaterial :
    entity === 'jobDetailBvA' ? columnsActualvsBudgetMaterial :
    columnsMaterial
  );

  const PopoverButton = () => {
    const [anchorEl, setAnchorEl] = useState(null);
  
    const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
    };
  
    const handleClose = () => {
      setAnchorEl(null);
    };
  
    const open = Boolean(anchorEl);
    const id = open ? "simple-popover" : undefined;
  
    return (
      <div>
        <Button
          variant="outlined"
          color={theme.palette.mode === 'dark' ? "secondary" : "primary"}
          size="small"
          endIcon={<ArrowDropDownIcon />}
          aria-describedby={id}
          onClick={handleClick}
          sx={{ backgroundColor: "#fff" }}
        >
          Add
        </Button>
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <Box sx={{ py: 2 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box sx={dropdownItemsStyle} onClick={handleAddLineItemOpenAsset}>
                <Icon icon="tabler:augmented-reality" />
                <Typography variant="h6">Asset</Typography>
              </Box>
              <Box sx={dropdownItemsStyle} onClick={handleAddLineItemOpenObservation}>
                <Icon icon="tabler:device-tablet-search" />
                <Typography variant="h6">Observation</Typography>
              </Box>
              <Box sx={dropdownItemsStyle} onClick={handleAddLineItemOpenLabor}>
                <LaborIcon />
                <Typography variant="h6">Labor</Typography>
              </Box>
              <Box sx={dropdownItemsStyle} onClick={handleAddLineItemOpenSubcontractor}>
                <LaborIcon />
                <Typography variant="h6">Subcontractor</Typography>
              </Box>
              <Box sx={dropdownItemsStyle}  onClick={handleAddLineItemOpenMaterial}>
                <Icon icon="tabler:cube" />
                <Typography variant="h6">Material</Typography>
              </Box>
              <Box sx={dropdownItemsStyle} onClick={handleAddLineItemOpenOtherCosts}>
                <OtherCostsIcon />
                <Typography variant="h6">Other Costs</Typography>
              </Box>
            </Box>
          </Box>
        </Popover>
      </div>
    );
  };

  const PopoverButtonFilter = () => {
    const [anchorEl, setAnchorEl] = useState(null);
  
    const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
    };
  
    const handleClose = () => {
      setAnchorEl(null);
    };
  
    const open = Boolean(anchorEl);
    const id = open ? "simple-popover" : undefined;
  
    return (
      <div>
        <Button
          variant="outlined"
          color={theme.palette.mode === 'dark' ? "secondary" : "primary"}
          endIcon={<KeyboardArrowDownOutlinedIcon />}
          aria-describedby={id}
          onClick={handleClick}
        >
          Filter By
        </Button>
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <Box sx={{ py: 2 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box sx={dropdownItemsStyle}>
                {/* <Icon icon="tabler:device-tablet-search" /> */}
                <Typography variant="h6">All</Typography>
              </Box>
              <Box sx={dropdownItemsStyle}>
                {/* <LaborIcon /> */}
                <Typography variant="h6">Employer</Typography>
              </Box>
              <Box sx={dropdownItemsStyle}>
                {/* <Icon icon="tabler:cube" /> */}
                <Typography variant="h6">Sub Contractor</Typography>
              </Box>
            </Box>
          </Box>
        </Popover>
      </div>
    );
  };
  //#endregion States and Constants

  //#region Handlers
  useEffect(() => {
    if (forceEmpty) {
      setFilteredRows([]);
      return;
    }
    if (entity === "jobDetailBvA") {
      if (buttonName === "Material") {
        setFilteredRows(jobMaterialList?.map(mapMaterialBvARow));
      } else if (buttonName === "Labor") {
        setFilteredRows(jobLaborList?.map(mapLaborBvARow));
      } else if(buttonName === "Subcontractor"){
        setFilteredRows(jobSubcontractorList?.map(mapSubcontractorBvARow));
      } else if (buttonName === "Assets") {
        setFilteredRows(jobAssetList?.map(mapAssetBvARow));
      } else if (buttonName === "Other Cost") {
        setFilteredRows(jobMiscCostList?.map(mapOtherCostBvARow));
      } else {
        setFilteredRows([]);
      }
    } else if (entity === "jobDetailActual") {
      if (buttonName === "Material") {
        setFilteredRows(jobMaterialList?.map(mapMaterialActualRow));
      } else if (buttonName === "Labor") {
        setFilteredRows(jobLaborList?.map(mapLaborActualRow));
      } else if (buttonName === "Subcontractor") {
        setFilteredRows(jobSubcontractorList?.map(mapSubcontractorActualRow));
      } else if (buttonName === "Assets") {
        setFilteredRows(jobAssetList?.map(mapAssetActualRow));
      } else if (buttonName === "Other Cost") {
        setFilteredRows(jobMiscCostList?.map(mapOtherCostActualRow));
      } else {
        setFilteredRows([]);
      }
    } else if (entity === "estimate-detail" || entity === "estimate" || entity === "workOrder" || entity === "jobDetail") {
      if (buttonName === "Material") {
        setFilteredRows(jobMaterialList);        
      } else if (buttonName === "Labor") {
        setFilteredRows(jobLaborList);
      } else if (buttonName === "Subcontractor") {
        setFilteredRows(jobSubcontractorList);
      } else if (buttonName === "Assets") {
        setFilteredRows(jobAssetList);
      } else if (buttonName === "Other Cost") {
        setFilteredRows(jobMiscCostList);
      } else {
        setFilteredRows([]);
      }
    } else {
      setFilteredRows([]);
    }
  }, [entity, buttonName, jobMaterialList, jobLaborList, jobSubcontractorList, jobAssetList, jobMiscCostList, forceEmpty]);

  const handleFilterTypeChange = (buttonIndex, buttonName) => {
    setSelectedButton(buttonIndex);
    setButtonName(buttonName);
  
    const key = `${buttonName}_${entity}`;
  
    const columnMap = {
      // Material
      "Material_default": columnsMaterial,
      "Material_jobDetail": columnsMaterial,
      "Material_jobDetailBvA": columnsActualvsBudgetMaterial,
      "Material_jobDetailActual": columnsActualMaterial,        // <--- Add this
    
      // Labor
      "Labor_default": columnsLabor,
      "Labor_jobDetail": columnsLabor,
      "Labor_jobDetailBvA": columnsActualvsBudgetLabor,
      "Labor_jobDetailActual": columnsActualLabor,              // <--- Add this
    
      // Assets
      "Assets_default": columnsAssets,
      "Assets_detail": columnsAssets,
      "Assets_jobDetail": columnsAssets,
      "Assets_jobDetailBvA": columnsActualvsBudgetAssets,
      "Assets_jobDetailActual": columnsActualAssets,            // <--- Add this
    
      // Other Cost
      "Other Cost_default": columnsOtherCost,
      "Other Cost_jobDetail": columnsOtherCost,
      "Other Cost_jobDetailBvA": columnsActualvsBudgetOtherCost,
      "Other Cost_jobDetailActual": columnsActualOtherCost,     // <--- Add this
    
      // Sub Contractor (add actual if you implement it)
      "Subcontractor_default": columnsSubcontractor,
      "Subcontractor_jobDetail": columnsSubcontractor,
      "Subcontractor_jobDetailBvA": columnsActualvsBudgetSubContractor,
      "Subcontractor_jobDetailActual": columnsActualSubContractor, // If you have it
    };
    
  
    // Determine key with fallback to 'default'
    const resolvedKey = columnMap[`${buttonName}_${entity}`]
      ? `${buttonName}_${entity}`
      : `${buttonName}_default`;
  
    setColumns(columnMap[resolvedKey] || columnsMaterial); // final fallback    
  };

  const boxStyle = {
    border: "1px solid #D0D5DD",
    borderRadius: 1,
    px: 4,
    py: 2,
    backgroundColor: "#F6F7FA",
  };
  

  const LineItemObservationList = () => {
    return (
      <>
      {jobObservationList && jobObservationList.length > 0 && (
        <Grid item xs={12}>
          <Box
            sx={{
              backgroundColor: "#fff",
              px: 6,
              py: 3,
              border: "1px solid #E8E8E8",
              borderRadius: "4px",
            }}
          >
            {/* Header Row */}            
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                mb: 2,
                borderBottom: "1px solid #D0D5DD",
                pb: 1,
              }}
            >
              <Box sx={{ width: "40%" }}>
                <FormLabel sx={{ fontSize: "13px", color: "text.primary", fontWeight: 600 }}>
                  OBSERVATION NAME
                </FormLabel>
              </Box>
              <Box sx={{ width: "50%" }}>
                <FormLabel sx={{ fontSize: "13px", color: "text.primary", fontWeight: 600 }}>
                  DESCRIPTION
                </FormLabel>
              </Box>              
              <Box sx={{ width: "10%" }}>
                <FormLabel sx={{ fontSize: "13px", color: "text.primary", fontWeight: 600 }}>
                  ACTIONS
                </FormLabel>
              </Box>
            </Box>            
            {/* Line Items */}
            {Array.isArray(jobObservationList) ? jobObservationList.map((jobObservation, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  mb: 2,
                }}
              >
                <Box sx={{ width: "40%" }}>
                  <Box sx={{ ...boxStyle }}>
                    <Typography variant="body2">{jobObservation.observationName}</Typography>
                  </Box>
                </Box>
                <Box sx={{ width: "50%" }}>
                  <Box sx={{ ...boxStyle }}>
                    <Typography variant="body2">{jobObservation.observationDescription}</Typography>
                  </Box>
                </Box>                
                <Box sx={{ width: "10%" }}>
                  <Box
                    sx={{
                      border: "1px solid #D0D5DD",
                      borderRadius: "8px",
                      px: 2,
                      py: 1,
                      backgroundColor: "#F6F7FA",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      minHeight: "40px", // Match height of other input boxes                      
                    }}
                  >
                    <IconButton
                      title="Edit"
                      color="secondary"
                      size="small"
                      sx={{ p: 0.5 }}
                      onClick={() => handleJobObservationEdit(jobObservation)}
                    >
                      <Icon icon="tabler:pencil" />
                    </IconButton>
                    <IconButton
                      title="Delete"
                      color="error"
                      size="small"
                      sx={{ p: 0.5 }}
                      onClick={() => handleJobObservationDelete(jobObservation.observationID)}
                    >
                      <Icon icon="tabler:trash" />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            )): null}
          </Box>
        </Grid>
        )}
      </>
    );
  }

  const LineItemAssetList = () => {
    return (
      <>
      {jobAssetList && jobAssetList.length > 0 && (
        <Grid item xs={12}>
          <Box
            sx={{
              backgroundColor: "#fff",
              px: 6,
              py: 3,
              border: "1px solid #E8E8E8",
              borderRadius: "4px",
            }}
          >
            {/* Header Row */}            
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                mb: 2,
                borderBottom: "1px solid #D0D5DD",
                pb: 1,
              }}
            >
              <Box sx={{ width: "30%" }}>
                <FormLabel sx={{ fontSize: "13px", color: "text.primary", fontWeight: 600 }}>
                  ASSET TYPE
                </FormLabel>
              </Box>
              <Box sx={{ width: "30%" }}>
                <FormLabel sx={{ fontSize: "13px", color: "text.primary", fontWeight: 600 }}>
                  ASSET NAME
                </FormLabel>
              </Box>
              <Box sx={{ width: "15%" }}>
                <FormLabel sx={{ fontSize: "13px", color: "text.primary", fontWeight: 600 }}>
                  QUANTITY
                </FormLabel>
              </Box>
              <Box sx={{ width: "15%" }}>
                <FormLabel sx={{ fontSize: "13px", color: "text.primary", fontWeight: 600 }}>
                  DURATION
                </FormLabel>
              </Box>
              <Box sx={{ width: "10%" }}>
                <FormLabel sx={{ fontSize: "13px", color: "text.primary", fontWeight: 600 }}>
                  ACTIONS
                </FormLabel>
              </Box>
            </Box>            
            {/* Line Items */}
            {Array.isArray(jobAssetList) ? jobAssetList.map((jobAsset, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  mb: 2,
                }}
              >
                <Box sx={{ width: "30%" }}>
                  <Box sx={{ ...boxStyle }}>
                    <Typography variant="body2">{jobAsset.assetTypeName}</Typography>
                  </Box>
                </Box>
                <Box sx={{ width: "30%" }}>
                  <Box sx={{ ...boxStyle }}>
                    <Typography variant="body2">{jobAsset.assetName}</Typography>
                  </Box>
                </Box>
                <Box sx={{ width: "15%" }}>
                  <Box sx={{ ...boxStyle }}>
                    <Typography variant="body2">{jobAsset.budgetedQuantity}</Typography>
                  </Box>
                </Box>
                <Box sx={{ width: "15%" }}>
                  <Box sx={{ ...boxStyle }}>
                    <Typography variant="body2">{jobAsset.budgetedDuration}</Typography>
                  </Box>
                </Box>
                <Box sx={{ width: "10%" }}>
                  <Box
                    sx={{
                      border: "1px solid #D0D5DD",
                      borderRadius: "8px",
                      px: 2,
                      py: 1,
                      backgroundColor: "#F6F7FA",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      minHeight: "40px", // Match height of other input boxes                      
                    }}
                  >
                    <IconButton
                      title="Edit"
                      color="secondary"
                      size="small"
                      sx={{ p: 0.5 }}
                      onClick={() => handleJobAssetEdit(jobAsset)}
                    >
                      <Icon icon="tabler:pencil" />
                    </IconButton>
                    <IconButton
                      title="Delete"
                      color="error"
                      size="small"
                      sx={{ p: 0.5 }}
                      onClick={() => handleJobAssetDelete(jobAsset.jobAssetID)}
                    >
                      <Icon icon="tabler:trash" />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            )): null}
          </Box>
        </Grid>
        )}
      </>
    );
  }

  const LineItemLaborList = () => {
    return (
      <>
      {jobLaborList && jobLaborList.length > 0 && (
        <Grid item xs={12}>
          <Box
            sx={{
              backgroundColor: "#fff",
              px: 6,
              py: 3,
              border: "1px solid #E8E8E8",
              borderRadius: "4px",
            }}
          >
            {/* Header Row1 */}            
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                mb: 2,
                borderBottom: "1px solid #D0D5DD",
                pb: 1,
              }}
            >
              <Box sx={{ width: "40%" }}>
                <FormLabel sx={{ fontSize: "13px", color: "text.primary", fontWeight: 600 }}>
                  LABOR SKILL
                </FormLabel>
              </Box>
              <Box sx={{ width: "40%" }}>
                <FormLabel sx={{ fontSize: "13px", color: "text.primary", fontWeight: 600 }}>
                  EMPLOYEE NAME
                </FormLabel>
              </Box>
              <Box sx={{ width: "10%" }}>
                <FormLabel sx={{ fontSize: "13px", color: "text.primary", fontWeight: 600 }}>
                  MANHRS
                </FormLabel>
              </Box>            
              <Box sx={{ width: "10%" }}>
                <FormLabel sx={{ fontSize: "13px", color: "text.primary", fontWeight: 600 }}>
                  ACTIONS
                </FormLabel>
              </Box>
            </Box>            
            {/* Line Items */}
            {Array.isArray(jobLaborList) ? jobLaborList.map((jobLabor, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  mb: 2,
                }}
              >
                <Box sx={{ width: "40%" }}>
                  <Box sx={{ ...boxStyle }}>
                    <Typography variant="body2">{jobLabor.skillType}</Typography>
                  </Box>
                </Box>
                <Box sx={{ width: "40%" }}>
                  <Box sx={{ ...boxStyle }}>
                    <Typography variant="body2">{employeeList.filter(x => x.employeeID === jobLabor.employeeID)[0]?.firstName+" "+employeeList.filter(x => x.employeeID === jobLabor.employeeID)[0]?.lastName}</Typography>
                  </Box>
                </Box>
                <Box sx={{ width: "10%" }}>
                  <Box sx={{ ...boxStyle }}>
                    <Typography variant="body2">{jobLabor.budgetedHours}</Typography>
                  </Box>
                </Box>                
                <Box sx={{ width: "10%" }}>
                  <Box
                    sx={{
                      border: "1px solid #D0D5DD",
                      borderRadius: "8px",
                      px: 2,
                      py: 1,
                      backgroundColor: "#F6F7FA",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      minHeight: "40px", // Match height of other input boxes                      
                    }}
                  >
                    <IconButton
                      title="Edit"
                      color="secondary"
                      size="small"
                      sx={{ p: 0.5 }}
                      onClick={() => handleJobLaborEdit(jobLabor)}
                    >
                      <Icon icon="tabler:pencil" />
                    </IconButton>
                    <IconButton
                      title="Delete"
                      color="error"
                      size="small"
                      sx={{ p: 0.5 }}
                      onClick={() => handleJobLaborDelete(jobLabor.jobLaborID)}
                    >
                      <Icon icon="tabler:trash" />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            )): null}
          </Box>
        </Grid>
        )}
      </>
    );
  }

  const LineItemMaterialList = () => {
    return (
      <>
      {jobMaterialList && jobMaterialList.length > 0 && (
        <Grid item xs={12}>
          <Box
            sx={{
              backgroundColor: "#fff",
              px: 6,
              py: 3,
              border: "1px solid #E8E8E8",
              borderRadius: "4px",
            }}
          >
            {/* Header Row */}            
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                mb: 2,
                borderBottom: "1px solid #D0D5DD",
                pb: 1,
              }}
            >
              <Box sx={{ width: "30%" }}>
                <FormLabel sx={{ fontSize: "13px", color: "text.primary", fontWeight: 600 }}>
                  ITEM TYPE
                </FormLabel>
              </Box>
              <Box sx={{ width: "30%" }}>
                <FormLabel sx={{ fontSize: "13px", color: "text.primary", fontWeight: 600 }}>
                  ITEM NAME
                </FormLabel>
              </Box>
              <Box sx={{ width: "10%" }}>
                <FormLabel sx={{ fontSize: "13px", color: "text.primary", fontWeight: 600 }}>
                  QTY
                </FormLabel>
              </Box>
              <Box sx={{ width: "20%" }}>
                <FormLabel sx={{ fontSize: "13px", color: "text.primary", fontWeight: 600 }}>
                  UNIT QTY
                </FormLabel>
              </Box>
              <Box sx={{ width: "10%" }}>
                <FormLabel sx={{ fontSize: "13px", color: "text.primary", fontWeight: 600 }}>
                  ACTIONS
                </FormLabel>
              </Box>
            </Box>            
            {/* Line Items */}
            {Array.isArray(jobMaterialList) ? jobMaterialList.map((jobMaterial, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  mb: 2,
                }}
              >
                <Box sx={{ width: "30%" }}>
                  <Box sx={{ ...boxStyle }}>
                    <Typography variant="body2">{jobMaterial.materialTypeName}</Typography>
                  </Box>
                </Box>
                <Box sx={{ width: "30%" }}>
                  <Box sx={{ ...boxStyle }}>
                    <Typography variant="body2">{jobMaterial.materialName}</Typography>
                  </Box>
                </Box>
                <Box sx={{ width: "10%" }}>
                  <Box sx={{ ...boxStyle }}>
                    <Typography variant="body2">{jobMaterial.budgetedQuantity}</Typography>
                  </Box>
                </Box>
                <Box sx={{ width: "20%" }}>
                  <Box sx={{ ...boxStyle }}>
                    <Typography variant="body2">{jobMaterial.unitOfMeasure}</Typography>
                  </Box>
                </Box>
                <Box sx={{ width: "10%" }}>
                  <Box
                    sx={{
                      border: "1px solid #D0D5DD",
                      borderRadius: "8px",
                      px: 2,
                      py: 1,
                      backgroundColor: "#F6F7FA",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      minHeight: "40px", // Match height of other input boxes                      
                    }}
                  >
                    <IconButton
                      title="Edit"
                      color="secondary"
                      size="small"
                      sx={{ p: 0.5 }}
                      onClick={() => handleJobMaterialEdit(jobMaterial)}
                    >
                      <Icon icon="tabler:pencil" />
                    </IconButton>
                    <IconButton
                      title="Delete"
                      color="error"
                      size="small"
                      sx={{ p: 0.5 }}
                      onClick={() => handleJobMaterialDelete(jobMaterial.jobMaterialID)}
                    >
                      <Icon icon="tabler:trash" />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            )): null}
          </Box>
        </Grid>
        )}
      </>
    );
  }

  const LineItemMiscCostList = () => {
    return (
      <>
      {jobMiscCostList && jobMiscCostList.length > 0 && (
        <Grid item xs={12}>
          <Box
            sx={{
              backgroundColor: "#fff",
              px: 6,
              py: 3,
              border: "1px solid #E8E8E8",
              borderRadius: "4px",
            }}
          >
            {/* Header Row */}            
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                mb: 2,
                borderBottom: "1px solid #D0D5DD",
                pb: 1,
              }}
            >
              <Box sx={{ width: "40%" }}>
                <FormLabel sx={{ fontSize: "13px", color: "text.primary", fontWeight: 600 }}>
                  OTHER COST
                </FormLabel>
              </Box>
              <Box sx={{ width: "50%" }}>
                <FormLabel sx={{ fontSize: "13px", color: "text.primary", fontWeight: 600 }}>
                  DESCRIPTION
                </FormLabel>
              </Box>              
              <Box sx={{ width: "10%" }}>
                <FormLabel sx={{ fontSize: "13px", color: "text.primary", fontWeight: 600 }}>
                  ACTIONS
                </FormLabel>
              </Box>
            </Box>            
            {/* Line Items */}
            {Array.isArray(jobMiscCostList) ? jobMiscCostList.map((jobMiscCost, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  mb: 2,
                }}
              >
                <Box sx={{ width: "40%" }}>
                  <Box sx={{ ...boxStyle }}>
                    <Typography variant="body2">{jobMiscCost.costName}</Typography>
                  </Box>
                </Box>
                <Box sx={{ width: "50%" }}>
                  <Box sx={{ ...boxStyle }}>
                    <Typography variant="body2">{jobMiscCost.costDescription}</Typography>
                  </Box>
                </Box>                
                <Box sx={{ width: "10%" }}>
                  <Box
                    sx={{
                      border: "1px solid #D0D5DD",
                      borderRadius: "8px",
                      px: 2,
                      py: 1,
                      backgroundColor: "#F6F7FA",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      minHeight: "40px", // Match height of other input boxes                      
                    }}
                  >
                    <IconButton
                      title="Edit"
                      color="secondary"
                      size="small"
                      sx={{ p: 0.5 }}
                      onClick={() => handleJobMiscCostEdit(jobMiscCost)}
                    >
                      <Icon icon="tabler:pencil" />
                    </IconButton>
                    <IconButton
                      title="Delete"
                      color="error"
                      size="small"
                      sx={{ p: 0.5 }}
                      onClick={() => handleJobMiscCostDelete(jobMiscCost.miscCostID)}
                    >
                      <Icon icon="tabler:trash" />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            )): null}
          </Box>
        </Grid>
        )}
      </>
    );
  }
  
  //#endregion Handlers

  return (
    <>    
      {entity === "assessment" && (           
          <>        
          <Grid container spacing={4}>
          <Grid item xs={12}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              <PopoverButton />
            </Box>
          </Grid>
            <LineItemAssetList />
            <LineItemObservationList />
            <LineItemMaterialList />
            <LineItemLaborList />
            <LineItemMiscCostList />
          </Grid>
          </>        
      )}
      {(entity === "estimate" ||
        entity === "estimate-detail" ||
        entity === "workOrder" ||
        entity === "workOrder-detail" ||
        entity === "service" ||
        entity === "jobDetail" ||
        entity === 'jobDetailBvA' ||
        entity === 'jobDetailActual' 
      ) && (
        <>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  flexDirection: { xs: "column", md: "row" },
                  alignItems: { xs: "stretch", md: "center" },
                  gap: { xs: 2, md: 0 },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    gap: 1,
                    width: { xs: "100%", md: "auto" }, 
                  }}
                >
                  {buttons.map((button, index) => (
                    <Button
                      key={index}
                      color={selectedButton === index ? "main" : "secondary"}
                      variant={selectedButton === index ? "contained" : "tonal"}
                      onClick={() => handleFilterTypeChange(index, button.name)}
                    >
                      {button.name}
                    </Button>
                  ))}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection:{ xs: "column", md: "row" },
                    alignItems: "center",
                    gap: 2,
                    mb:2,
                  }}
                >
                  {entity !== 'jobDetailBvA' && entity !== 'jobDetail' && entity !== 'jobDetailActual' && (
                    <PopoverButtonFilter />
                  )}
                  {(entity === "estimate" ||
                    entity === "estimate-detail" ||
                    entity === "workOrder" ||
                    entity === "service" ||
                    entity === "jobDetail" ||
                    entity === "jobDetailActual") && !disableEdit && (
                  <Button variant="outlined" color={theme.palette.mode === 'dark' ? 'secondary' : 'primary'} startIcon={<AddIcon />}
                    onClick={() => {
                      if (buttonName === "Material") {
                        handleAddLineItemOpenMaterial();
                      }
                      else if (buttonName === "Labor") {
                        handleAddLineItemOpenLabor();
                      }
                      else if (buttonName === "Assets") {
                        handleAddLineItemOpenAsset();
                      }
                      else if (buttonName === "Other Cost") {
                        handleAddLineItemOpenOtherCosts();
                      }
                      else if (buttonName === "Subcontractor") {
                        handleAddLineItemOpenSubcontractor();
                      }
                    }}
                  >
                    Add {buttonName}                    
                  </Button>
                  )}
                </Box>
              </Box>
            </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                maxWidth: "100%",
                overflowX: "auto",
              }}
            >
              {/* {console.log(columns, filteredRows)} */}
              <DataGridPro
                editMode="row"
                pagination
                pageSizeOptions={5}
                rows={filteredRows}
                rowHeight={62}
                columns={columns}
                getRowId={(row) => row.id}
                disableRowSelectionOnClick
                getRowClassName={(params) =>
                  params.id === "summary" ? "summary-row" : ""
                }
                sx={{
                  minHeight: 200,
                  maxHeight: 500,
                  "& .MuiDataGrid-columnHeaderTitle": {
                    textTransform: "none",
                  },
                  "& .summary-row": {
                    backgroundColor: "#F8FAFC",
                  },
                  '& .MuiTooltip-popper': { zIndex: 1500 }
                }}
              />
            </Box>
          </Grid>
        </>
      )}

      <AddLineItem 
        open={openAddLineItem}
        handleClose={handleLineItemClose}
        entity={entity}
        {...LineItemProps}
        {...LineItemObservationProps}
        {...LineItemMaterialProps}
        {...LineItemLaborProps}
        {...LineItemSubcontractorProps}
        {...LineItemMiscCostProps}
      />
    </>
  );
};

export default LineItems;
